/* Hallmark · component: form · genre: modern-minimal · theme: project-tokens
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P4 H4 E4 S5 R5 V3. Component scope, existing tokens.
 * Rack settings: changed schema fields only; unknown values remain unknown.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Save, ShieldAlert, Server, X } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { rackUpdatePayload, rackDisplayTitle, type RackEditForm } from '../../utils/rackEdit.logic';
import './RackEditModal.css';

const inputCls = 'rack-settings-input'

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-xs font-medium mb-2"
      style={{ color: 'var(--text-secondary)' }}
    >
      {children}
    </label>
  )
}

function SectionDiv({ label }: { label: string }) {
  return (
    <h3 className="rack-settings-section">{label}</h3>
  )
}

interface RackEditModalProps {
  rack: any;
  datacenterCode: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const RackEditModal: React.FC<RackEditModalProps> = ({
  rack,
  onClose,
  onSuccess,
}) => {
  const [initial] = useState<RackEditForm>(() => ({
    heightU: rack.u_slots ?? null, type: rack.type ?? '', isBlocked: Boolean(rack.is_blocked),
    tenant: rack.tenant ?? null, maxWeightKg: rack.max_weight_kg ?? null, maxPowerW: rack.max_power_w ?? null,
    widthM: rack.width_m ?? null, depthM: rack.depth_m ?? null,
  }))
  const initialHeightU = initial.heightU
  const [heightU, setHeightU] = useState(initialHeightU)
  const [type, setType] = useState(initial.type)
  const [isBlocked, setIsBlocked] = useState(initial.isBlocked)
  const [tenant, setTenant] = useState(initial.tenant)
  const [cascadeTenant, setCascadeTenant] = useState(false)
  const [maxWeightKg, setMaxWeightKg] = useState(initial.maxWeightKg)
  const [maxPowerW, setMaxPowerW] = useState(initial.maxPowerW)
  const [widthM, setWidthM] = useState(initial.widthM)
  const [depthM, setDepthM] = useState(initial.depthM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const element = dialogRef.current
    const previousFocus = document.activeElement as HTMLElement | null
    element?.showModal()
    return () => {
      element?.close()
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    const payload = rackUpdatePayload({ type, heightU, isBlocked, tenant, cascadeTenant,
      maxWeightKg, maxPowerW, widthM, depthM }, initial)
    if (!Object.keys(payload).length) { onClose(); return }
    setSaving(true)
    setError('')
    try {
      await api.put(`/api/datacenters/racks/${rack.id}`, payload, { timeout: 30000 })
      toast.success('Параметры стойки обновлены')
      onSuccess()
      onClose()
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Подтверждение сохранения не получено. Проверьте данные и повторите попытку.')
    } finally {
      setSaving(false)
    }
  }

  const rackTitle = rackDisplayTitle(rack)
  const unconfirmed = rack?.address_confirmed !== true && !rack?.is_facility && !['pillar', 'column'].includes(rack?.type)

  return (
    <dialog ref={dialogRef} className="rack-settings-sheet" aria-labelledby="rack-settings-title"
      onCancel={event => { event.preventDefault(); if (!saving) onClose() }}
      onKeyDown={event => {
        event.stopPropagation()
        if (event.key !== 'Tab') return
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, input, select')].filter(el => !el.matches(':disabled'))
        const first = controls[0], last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }}>
      <header className="rack-settings-header">
        <div><h2 id="rack-settings-title">Параметры стойки</h2><p>{rackTitle}</p></div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Закрыть панель" title="Закрыть панель" autoFocus><X size={18} aria-hidden /></button>
      </header>
      <form id="rack-edit-form" onSubmit={handleSubmit} className="rack-settings-body">
        {error && <p role="alert" className="rack-settings-error">{error}</p>}
        <fieldset disabled={saving} className="flex flex-col gap-4">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Server size={16} style={{ color: 'var(--text-secondary)' }} aria-hidden />
            <h3 className="text-sm font-semibold">Идентификация</h3>
          </div>
          <div>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>ID стойки: {rack.id}</p>
            <FieldLabel htmlFor="rack-edit-label">Сохранённая метка</FieldLabel>
            <input
              id="rack-edit-label"
              type="text"
              value={rack?.label ?? ''}
              placeholder="Метка не задана"
              aria-describedby={unconfirmed ? 'rack-edit-address-warning' : undefined}
              readOnly
              className={inputCls}
            />
            {unconfirmed && <div id="rack-edit-address-warning" className="mt-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
              <p className="font-semibold">Место не подтверждено</p>
              <p className="mt-1">Метка не является действующим адресом стойки.</p>
            </div>}
          </div>
        </div>

        <SectionDiv label="Характеристики" />

        <div className="rack-settings-grid">
          <div>
            <FieldLabel htmlFor="rack-edit-height">Вместимость (U)</FieldLabel>
            <input type="number" min="0" step="1" list="rack-edit-capacities" placeholder="Не указана"
              id="rack-edit-height"
              aria-describedby="rack-edit-physical-height"
              value={heightU ?? ''}
              onChange={e => setHeightU(e.target.value === '' ? null : e.target.valueAsNumber)}
              className={inputCls}
            />
            <datalist id="rack-edit-capacities">{[24, 42, 45, 48, 52].map(value => <option key={value} value={value} />)}</datalist>
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-type">Тип стойки</FieldLabel>
            <select
              id="rack-edit-type"
              value={type}
              onChange={e => setType(e.target.value)}
              className={inputCls}
            >
              {!['server_rack', 'network_rack', 'telecom', 'pdu_frame', 'open_frame'].includes(initial.type) && <option value={initial.type}>{initial.type || 'Не указан'}</option>}
              <option value="server_rack">Серверная</option>
              <option value="network_rack">Сетевая</option>
              <option value="open_frame">Открытая рама</option>
              <option value="telecom">Телеком шкаф</option>
              <option value="pdu_frame">PDU Frame</option>
            </select>
          </div>
          <p id="rack-edit-physical-height" className="col-span-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
            Изменение U не меняет физическую высоту стойки.
          </p>
          <div>
            <FieldLabel htmlFor="rack-edit-width">Ширина (м)</FieldLabel>
            <input id="rack-edit-width" type="number" step="any" min="0.001" value={widthM ?? ''} placeholder="Не указана"
              onChange={e => setWidthM(e.target.value === '' ? null : e.target.valueAsNumber)} className={inputCls} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-depth">Глубина (м)</FieldLabel>
            <input id="rack-edit-depth" type="number" step="any" min="0.001" value={depthM ?? ''} placeholder="Не указана"
              onChange={e => setDepthM(e.target.value === '' ? null : e.target.valueAsNumber)} className={inputCls} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-weight">Макс. масса (кг)</FieldLabel>
            <input id="rack-edit-weight" type="number" step="any" min="0" value={maxWeightKg ?? ''} placeholder="Не указана"
              onChange={e => setMaxWeightKg(e.target.value === '' ? null : e.target.valueAsNumber)} className={inputCls} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-power">Макс. мощность (Вт)</FieldLabel>
            <input id="rack-edit-power" type="number" step="any" min="0" value={maxPowerW ?? ''} placeholder="Не указана"
              onChange={e => setMaxPowerW(e.target.value === '' ? null : e.target.valueAsNumber)} className={inputCls} />
          </div>
          <div className="col-span-2">
            <FieldLabel htmlFor="rack-edit-tenant">Тенант</FieldLabel>
            <input id="rack-edit-tenant" list="rack-edit-tenants" value={tenant ?? ''} placeholder="Не указан" pattern=".*\S.*" title="Укажите тенанта или оставьте поле пустым"
              onChange={e => { setTenant(e.target.value === '' ? null : e.target.value); setCascadeTenant(false) }}
              className={inputCls} />
            <datalist id="rack-edit-tenants"><option value="mine" /><option value="other" /><option value="reserved" /></datalist>
            <label className="rack-settings-checkbox">
              <input
                type="checkbox"
                checked={cascadeTenant}
                disabled={!tenant || !tenant.trim()}
                onChange={e => setCascadeTenant(e.target.checked)}
              />
              <span>Применить владельца ко всему установленному оборудованию</span>
            </label>
          </div>
        </div>

        {(widthM == null || depthM == null) && <p role="status" className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Ширина или глубина неизвестна. Контур оценочный; физическое размещение не подтверждено.
        </p>}

        <SectionDiv label="Доступность" />

        <label htmlFor="rack-edit-blocked" className="rack-settings-checkbox">
          <input id="rack-edit-blocked" type="checkbox" checked={isBlocked} onChange={e => setIsBlocked(e.target.checked)} />
          <ShieldAlert size={16} style={{ color: 'var(--text-secondary)' }} aria-hidden />
          <span>Заблокировать стойку</span>
        </label>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Запретит установку нового оборудования в эту стойку</p>
        </fieldset>
      </form>
      <footer className="rack-settings-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Отмена
          </button>
          <button
            type="submit"
            form="rack-edit-form"
            disabled={saving}
            className="rack-settings-save"
            aria-busy={saving}
          >
            <Save size={16} aria-hidden />
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
      </footer>
    </dialog>
  )
}
